/**
 * DailyAllowanceCard — "What can I actually spend today?"
 *
 * Integrates bank balance + unpaid bills + variable budget into one number.
 * Two modes:
 *   Bank mode:   starts from real bank balance, subtracts unpaid bills,
 *                caps at budget remaining → most conservative daily allowance
 *   Budget mode: fallback when no bank balance is set (budget ÷ days left)
 *
 * Includes inline bank-balance update so users never have to leave this card.
 */
import React, { useState } from 'react';
import { fc } from '../../utils';

const f0 = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);

function Row({ label, value, col, bold, note, dim, divider }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: divider ? '8px 0 4px' : '4px 0',
      borderTop: divider ? '1.5px solid var(--bdr)' : 'none',
      marginTop: divider ? 4 : 0,
      opacity: dim ? 0.55 : 1,
    }}>
      <span style={{ fontSize: 12, color: 'var(--mut)', fontWeight: bold ? 700 : 400 }}>{label}</span>
      <div style={{ textAlign: 'right' }}>
        <span style={{ fontSize: bold ? 14 : 12, fontWeight: bold ? 800 : 600, color: col || 'var(--txt)' }}>
          {value}
        </span>
        {note && <div style={{ fontSize: 10, color: 'var(--mut)', marginTop: 1 }}>{note}</div>}
      </div>
    </div>
  );
}

export function DailyAllowanceCard({
  budgetRem, varBudget, vari,
  daysInMonth, dayOfMonth,
  bankBalance = 0, bankBalanceDate = null,
  bankCreditsTotal = 0,
  unpaidBillsTotal = 0, unpaidBillsCount = 0,
  updateState,
}) {
  const [editingBank, setEditingBank] = useState(false);
  const [bankInput, setBankInput] = useState('');
  const [showBreakdown, setShowBreakdown] = useState(false);

  if (varBudget <= 0) return null;

  const today = new Date();
  const todayStr = today.toISOString().slice(0, 10);
  const daysLeft = Math.max(1, daysInMonth - dayOfMonth + 1);
  const pastDays  = Math.max(1, dayOfMonth - 1);
  const dailyPace = dayOfMonth > 1 && vari > 0 ? vari / pastDays : null;

  // ── Core calculation ───────────────────────────────────────────────────────
  const bankMode = bankBalance > 0;
  const afterBills = bankBalance + bankCreditsTotal - unpaidBillsTotal;
  const bankCritical = bankMode && afterBills < 0; // bank can't cover remaining bills

  // Daily allowance: use whichever is more constraining — bank reality or budget plan
  const spendableBudget = bankMode
    ? Math.min(Math.max(0, afterBills), Math.max(0, budgetRem))
    : Math.max(0, budgetRem);

  const allowance = spendableBudget / daysLeft;
  const isOver   = allowance < 0 || (budgetRem < 0);
  const isTight  = !isOver && allowance < 15;
  const color    = bankCritical || isOver ? 'var(--red)' : isTight ? 'var(--ylw)' : 'var(--grn)';

  // Pace vs target
  const paceOver    = dailyPace !== null && dailyPace > allowance + 2;
  const paceUnder   = dailyPace !== null && dailyPace < allowance - 2;
  const paceColor   = paceOver ? 'var(--red)' : paceUnder ? 'var(--grn)' : 'var(--ylw)';

  // End-of-month projection at current pace
  const projectedTotal = dailyPace !== null ? vari + dailyPace * daysLeft : null;

  // "Save more" suggestion: what if you spent your target instead?
  const extraSavings = dailyPace !== null && paceOver
    ? Math.round((dailyPace - allowance) * daysLeft * 100) / 100
    : null;

  const bankDateLabel = bankBalanceDate
    ? (() => {
        const d = new Date(bankBalanceDate + 'T12:00:00');
        const diffDays = Math.floor((today - d) / 86400000);
        return diffDays === 0 ? 'today' : diffDays === 1 ? 'yesterday' : `${diffDays}d ago`;
      })()
    : null;

  const saveBank = () => {
    const v = parseFloat(bankInput);
    if (isNaN(v) || v < 0) return;
    updateState?.({ bankBalance: Math.round(v * 100) / 100, bankBalanceDate: todayStr });
    setEditingBank(false);
    setBankInput('');
  };

  return (
    <div className="card cp" style={{ marginBottom: 12, borderLeft: `3px solid ${color}` }}>

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--mut)', textTransform: 'uppercase', letterSpacing: '.5px' }}>
            💰 Daily Spending Target
          </div>
          <div style={{ fontSize: 10, color: 'var(--mut)', marginTop: 2 }}>
            {bankMode ? 'Bank + budget · real picture' : 'Budget only · add bank balance for full picture'}
          </div>
        </div>
        {/* Inline bank update button */}
        {updateState && (
          <button
            onClick={() => { setEditingBank((e) => !e); setBankInput(bankBalance > 0 ? String(bankBalance) : ''); }}
            style={{ fontSize: 10, fontWeight: 700, padding: '4px 9px', borderRadius: 7, border: '1.5px solid var(--bdr)', background: editingBank ? 'var(--surf2)' : 'none', color: 'var(--mut)', cursor: 'pointer', whiteSpace: 'nowrap' }}
          >
            {editingBank ? 'Cancel' : bankMode ? `🏦 ${f0(bankBalance)}${bankDateLabel ? ` · ${bankDateLabel}` : ''}` : '🏦 Add balance'}
          </button>
        )}
      </div>

      {/* ── Inline bank edit ───────────────────────────────────────────────── */}
      {editingBank && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'var(--surf2)', border: '2px solid var(--ylw)', borderRadius: 9, padding: '6px 12px', flex: 1 }}>
            <span style={{ fontSize: 13, color: 'var(--mut)', fontWeight: 700 }}>$</span>
            <input
              type="number" min="0" step="0.01" placeholder="Current balance"
              value={bankInput}
              onChange={(e) => setBankInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && saveBank()}
              autoFocus
              style={{ flex: 1, background: 'none', border: 'none', fontSize: 16, fontWeight: 700, color: 'var(--ylw)', fontFamily: 'inherit' }}
            />
          </div>
          <button onClick={saveBank} style={{ padding: '8px 14px', borderRadius: 9, background: 'var(--ylw)', color: '#000', fontWeight: 800, fontSize: 12, border: 'none', cursor: 'pointer' }}>
            Save
          </button>
        </div>
      )}

      {/* ── Critical bank warning ───────────────────────────────────────────── */}
      {bankCritical && (
        <div style={{ padding: '8px 12px', borderRadius: 8, background: 'rgba(255,107,107,.1)', border: '1px solid rgba(255,107,107,.3)', fontSize: 12, color: 'var(--red)', fontWeight: 700, marginBottom: 12 }}>
          ⚠️ Bank can't cover remaining bills — you're {f0(Math.abs(afterBills))} short. Pay off bills or add funds before spending more.
        </div>
      )}

      {/* ── Hero number ─────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, marginBottom: 6 }}>
        <div style={{ fontFamily: 'Syne,sans-serif', fontSize: 46, fontWeight: 900, color, lineHeight: 1 }}>
          {isOver || bankCritical ? '$0' : fc(allowance)}
        </div>
        <div style={{ paddingBottom: 8 }}>
          <div style={{ fontSize: 13, color: 'var(--mut)', fontWeight: 600 }}>per day</div>
          <div style={{ fontSize: 10, color: 'var(--mut)' }}>{daysLeft} day{daysLeft !== 1 ? 's' : ''} left</div>
        </div>
      </div>

      {/* ── Pace indicator ──────────────────────────────────────────────────── */}
      {dailyPace !== null && !bankCritical && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, padding: '8px 10px', borderRadius: 8, background: paceOver ? 'rgba(255,107,107,.07)' : paceUnder ? 'rgba(107,203,119,.07)' : 'rgba(255,217,61,.07)', border: `1px solid ${paceOver ? 'rgba(255,107,107,.2)' : paceUnder ? 'rgba(107,203,119,.2)' : 'rgba(255,217,61,.2)'}` }}>
          <span style={{ fontSize: 15 }}>{paceOver ? '⚠️' : paceUnder ? '🎉' : '✅'}</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: paceColor }}>
              {paceOver
                ? `Spending ${fc(dailyPace)}/day — ${fc(dailyPace - allowance)}/day over target`
                : paceUnder
                  ? `Spending ${fc(dailyPace)}/day — ${fc(allowance - dailyPace)}/day under target`
                  : `Spending ${fc(dailyPace)}/day — right on track`}
            </div>
            <div style={{ fontSize: 10, color: 'var(--mut)', marginTop: 1 }}>
              {paceOver && extraSavings
                ? `Hit your target → save ${f0(extraSavings)} more this month`
                : paceUnder && projectedTotal !== null
                  ? `On pace to come in ${f0(varBudget - projectedTotal)} under budget 🎯`
                  : projectedTotal !== null
                    ? `On pace for ${f0(projectedTotal)} this month`
                    : null}
            </div>
          </div>
        </div>
      )}

      {/* ── Breakdown toggle ────────────────────────────────────────────────── */}
      <button
        onClick={() => setShowBreakdown((s) => !s)}
        style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: 'var(--mut)', fontWeight: 600, padding: '4px 0', display: 'flex', alignItems: 'center', gap: 4, fontFamily: 'inherit' }}
      >
        {showBreakdown ? '▲ Hide' : '▼ Show'} how this is calculated
      </button>

      {/* ── Full breakdown ──────────────────────────────────────────────────── */}
      {showBreakdown && (
        <div style={{ marginTop: 8, padding: '12px', background: 'var(--surf2)', borderRadius: 10, border: '1px solid var(--bdr)' }}>
          {bankMode ? (
            <>
              <Row label="🏦 Bank balance" value={f0(bankBalance)} col="var(--txt)" bold note={bankDateLabel ? `Updated ${bankDateLabel}` : undefined} />
              {bankCreditsTotal > 0 && (
                <Row label="+ Planned credits" value={'+' + f0(bankCreditsTotal)} col="var(--grn)" />
              )}
              <Row
                label={`📋 Bills still due (${unpaidBillsCount})`}
                value={unpaidBillsCount > 0 ? '−' + f0(unpaidBillsTotal) : 'all paid ✓'}
                col={unpaidBillsCount > 0 ? 'var(--red)' : 'var(--grn)'}
              />
              <Row label="Safe floor" value={afterBills >= 0 ? f0(afterBills) : '−' + f0(Math.abs(afterBills))} col={afterBills < 0 ? 'var(--red)' : afterBills < 300 ? 'var(--ylw)' : 'var(--grn)'} bold divider note="what's yours after bills clear" />
              <Row label="📊 Variable budget" value={f0(varBudget)} col="var(--mut)" dim />
              <Row label="  − Already spent" value={'−' + f0(vari)} col="var(--mut)" dim />
              <Row label="  = Budget remaining" value={budgetRem >= 0 ? f0(budgetRem) : '−' + f0(Math.abs(budgetRem))} col={budgetRem < 0 ? 'var(--red)' : 'var(--mut)'} dim />
              {budgetRem < afterBills && afterBills > 0 && (
                <div style={{ fontSize: 10, color: 'var(--mut)', marginTop: 4, fontStyle: 'italic' }}>
                  Budget is tighter than your bank floor — budget drives your target
                </div>
              )}
              {afterBills < budgetRem && afterBills > 0 && (
                <div style={{ fontSize: 10, color: 'var(--mut)', marginTop: 4, fontStyle: 'italic' }}>
                  Bank floor is tighter than your budget — bank drives your target
                </div>
              )}
              <Row label={`Spendable ÷ ${daysLeft} days`} value={f0(spendableBudget) + ' ÷ ' + daysLeft} col="var(--mut)" divider />
              <Row label="= Daily target" value={fc(allowance)} col={color} bold />
            </>
          ) : (
            <>
              <Row label="📊 Variable budget" value={f0(varBudget)} col="var(--txt)" bold />
              <Row label="− Already spent" value={'−' + f0(vari)} col="var(--red)" />
              <Row label="= Remaining" value={budgetRem >= 0 ? f0(budgetRem) : '−' + f0(Math.abs(budgetRem))} col={budgetRem < 0 ? 'var(--red)' : 'var(--grn)'} bold divider />
              <Row label={`÷ ${daysLeft} days left`} value={fc(allowance)} col={color} bold />
              <div style={{ marginTop: 10, padding: '8px 10px', borderRadius: 8, background: 'rgba(255,217,61,.07)', border: '1px solid rgba(255,217,61,.2)', fontSize: 11, color: 'var(--ylw)' }}>
                💡 Add your bank balance (tap above) for a real-cash picture that accounts for unpaid bills
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Save more nudge ─────────────────────────────────────────────────── */}
      {!bankCritical && !isOver && extraSavings && extraSavings > 10 && (
        <div style={{ marginTop: 10, padding: '8px 12px', borderRadius: 8, background: 'rgba(107,203,119,.07)', border: '1px solid rgba(107,203,119,.2)', fontSize: 11, color: 'var(--grn)' }}>
          💵 Stick to {fc(allowance)}/day and pocket {f0(extraSavings)} extra at month end
        </div>
      )}
    </div>
  );
}
